import {Component, EventEmitter, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {HeaderService} from '../../core/services/header.service';
import {BaseComponent} from '../../core/base.component';
import {Title} from '@angular/platform-browser';
import {AuthService} from '../../core/security/auth.service';
import {ReportsService} from '../reports.service';
import {ReportsService as ReportsApiService} from '../../api/services/reports.service';
import {UsersSummaryGridComponent} from './users-summary-grid/users-summary-grid.component';
import {RolesEnum} from '../../core/security/roles.enum';
import {UsersDashboardService} from './users-dashboard.service';
import {UsersTotalModel} from '../../api/models/users-total-model';
import {appColors} from '../../core/styles/colors';
import {SelectedFilterValuesModel} from '../../dialogs/grid-filter/selected-filter-values.model';
import {FilterOptionModel} from '../../dialogs/grid-filter/filter-option.model';
import {DateRangeService} from '../../core/services/date-range.service';
import * as _ from 'lodash';
import {MatUsersSummaryFilterDialogComponent} from '../shared/mat-users-summary-filter-dialog/mat-users-summary-filter-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {MatChartByUserDialogComponent} from './charts/mat-chart-by-user-dialog.component';
import { FlexModule } from '@angular/flex-layout';
import { NgIf } from '@angular/common';
import { MatProgressBar } from '@angular/material/progress-bar';
import { GridFilterComponent } from '../../dialogs/grid-filter/grid-filter.component';
import { FilterRowAdditionalContentComponent } from '../../dialogs/grid-filter/filter-row-additional-content.component';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatCard, MatCardContent } from '@angular/material/card';
import { ChartByUserComponent } from './charts/chart-by-user.component';

@Component({
    selector: 'app-users-summary',
    templateUrl: './users-summary.component.html',
    styleUrls: ['./users-summary.component.scss'],
    standalone: true,
    imports: [
        FlexModule,
        NgIf,
        MatProgressBar,
        GridFilterComponent,
        FilterRowAdditionalContentComponent,
        MatButton,
        MatIcon,
        MatCard,
        MatCardContent,
        ChartByUserComponent,
        UsersSummaryGridComponent,
    ],
})
export class UsersSummaryComponent extends BaseComponent implements OnInit, OnDestroy {

  filterChangeEvent = new EventEmitter<any>();
  selectedFilterChange = new EventEmitter<SelectedFilterValuesModel[]>();

  selectedFilter: SelectedFilterValuesModel[] = new Array<SelectedFilterValuesModel>();
  filterOptions: FilterOptionModel[] = [];

  colors = appColors;

  dashboardData: UsersTotalModel = {} as UsersTotalModel;
  userSeriesTotals: any = [];
  userSeriesAvgTotals: any = [];
  isLoading = true;

  @ViewChild('usersSummaryGrid', {static: true}) usersSummaryGrid: UsersSummaryGridComponent;

  constructor(public reportsService: ReportsService,
              public dateRangeService: DateRangeService,
              public usersDashboardService: UsersDashboardService,
              public reportApiService: ReportsApiService,
              public dialog: MatDialog,
              route: ActivatedRoute, headerService: HeaderService, titleService: Title, authService: AuthService) {
    super(route, headerService, titleService, authService);

    this.editRoles = [RolesEnum.UsersSummary_Edit, RolesEnum.UsersSummary_Delete];
    this.deleteRoles = [RolesEnum.UsersSummary_Delete];
  }

  ngOnInit() {
    super.ngOnInit();
    this.headerService.hideSearchInput();

    this.dashboardData = this.usersDashboardService.dashboardData;
    this.mapData();

    if (this.reportsService.accountsLookup?.length > 0) {
      this.initFilterOptions();
    } else {
      this.reportsService.lookupUpdatedEvent.subscribe(() => {
        this.initFilterOptions();
      });
    }

    this.filterChangeEvent.subscribe(async (filter) => {
      console.log(filter);
      this.isLoading = true;
      const response = await this.reportApiService.usersSummaryGetUsersDashboardData({
        body: {
          domain: this.reportsService.selectedDomain!,
          startDate: this.dateRangeService.rangeStartMoment.toISO(),
          endDate: this.dateRangeService.rangeEndMoment.toISO(),
          extensions: filter.extensions
        }
      }).toPromise();

      if (response) {
        this.usersDashboardService.dashboardData = response;
        this.dashboardData = this.usersDashboardService.dashboardData;

        this.mapData();
      }
      this.isLoading = false;
    });
  }

  mapData() {
    this.dashboardData = this.usersDashboardService.dashboardData;

    if (!this.dashboardData.totalsByUser) {
      this.dashboardData.totalsByUser = [];
    }
    this.userSeriesTotals = this.dashboardData.totalsByUser.map((item) => {
      return {
        extension: item._id,
        answeredByMailbox: item.answeredByMailbox,
        answeredCalls: item.answeredCalls,
        answeredGroupCalls: item.answeredGroupCalls,
        canceled: item.canceled,
        outgoingCalls: item.outgoingCalls,
        avgTalkDuration: item.avgTalkDuration,
      };
    });
    this.sortUsersData();

    this.userSeriesAvgTotals = this.userSeriesTotals.slice().sort((left, right) => {
      let leftTotal, rightTotal;
      leftTotal = left.avgTalkDuration;
      rightTotal = right.avgTalkDuration;

      return leftTotal === rightTotal ? 0 : (leftTotal > rightTotal ? -1 : 1);
    });
  }

  sortUsersData() {
    const type = this.usersDashboardService.selectedFilterType.action;
    this.userSeriesTotals.sort((left, right) => {
      let leftTotal, rightTotal;
      if (type === 0) {
        leftTotal = left.answeredCalls;
        rightTotal = right.answeredCalls;
      } else if (type === 1) {
        leftTotal = left.outgoingCalls;
        rightTotal = right.outgoingCalls;
      } else if (type === 2) {
        leftTotal = left.canceled;
        rightTotal = right.canceled;
      } else if (type === 3) {
        leftTotal = left.answeredByMailbox;
        rightTotal = right.answeredByMailbox;
      } else {
        leftTotal = left.answeredCalls + left.outgoingCalls + left.canceled + left.answeredByMailbox;
        rightTotal = right.answeredCalls + right.outgoingCalls + right.canceled + right.answeredByMailbox;
      }
      return leftTotal === rightTotal ? 0 : (leftTotal > rightTotal ? -1 : 1);
    });
  }

  onSeriesChartZoomClick() {
    this.dialog.open(MatChartByUserDialogComponent, {
      width: '90%',
      height: '90%',
      data: {
        chartData: this.userSeriesAvgTotals,
        filterType: this.usersDashboardService.selectedFilterType,
        dialogTitle: 'Average Talk Time by Agent',
        showTimeSeries: true
      }
    });
  }

  onUserChartZoomClick() {
    this.dialog.open(MatChartByUserDialogComponent, {
      width: '90%',
      height: '90%',
      data: {
        chartData: this.userSeriesTotals,
        filterType: this.usersDashboardService.selectedFilterType,
        dialogTitle: this.usersDashboardService.selectedFilterType.text,
        showTimeSeries: false,
      }
    });
  }

  ngOnDestroy() {
    super.ngOnDestroy();
  }

  async openExportDialog() {
    await this.usersSummaryGrid.onExportClick();
  }

  onSelectedItemsChanged($event: SelectedFilterValuesModel[]) {
    this.selectedFilter = $event;
  }

  onFilterChanged(filter: any) {
    let extensions = [];

    if (filter['extensions']) {
      extensions = _.union(extensions, filter['extensions']);
      filter['extensions'] = undefined;
    }
    if (filter['departments']) {
      const departments = this.reportsService.getDepartmentsExtensions(filter['departments']);
      extensions = _.union(extensions, departments);
      filter['departments'] = undefined;
    }

    filter['extensions'] = extensions;

    console.log('onFilterChanged', filter);

    this.filterChangeEvent.emit(filter);
  }

  openFilterDialog() {
    console.log('open filter dialog');
    const dialogRef = this.dialog.open(MatUsersSummaryFilterDialogComponent, {
      width: '500px',
      height: '90%',
      data: {
        filter: this.selectedFilter
      }
    });

    dialogRef.afterClosed().subscribe(async (result: SelectedFilterValuesModel[]) => {
      if (result) {
        this.selectedFilterChange.emit(result);
      }
    });
  }

  initFilterOptions(): void {
    this.filterOptions = [
      {
        filterKey: 'searchText',
        multiValue: false,
        showAlways: true,
        type: 'string',
        title: 'Any'
      },
    ];

    if (this.reportsService.extensionsLookup.length > 0) {
      for (const item of this.reportsService.extensionsLookup) {
        this.filterOptions.push({
          filterKey: 'extensions',
          multiValue: true,
          showAlways: false,
          type: 'static',
          title: 'User',
          displayValue: item.text,
          value: item.value,
          searchText: `User: ${item.text}`,
        });
      }
    }

    if (this.reportsService.departmentsLookup.length > 0) {
      for (const item of this.reportsService.departmentsLookup) {
        this.filterOptions.push({
          filterKey: 'departments',
          multiValue: true,
          showAlways: false,
          type: 'static',
          title: 'Department',
          displayValue: item.text,
          value: item.value,
          searchText: `Department: ${item.text}`,
        });
      }
    }
  }

}
